import { keyBy } from "lodash";
import groupBy from "lodash/groupBy";
import partition from "lodash/partition";
import { all, put, select, takeEvery } from "redux-saga/effects";
import { v4 } from "uuid";

import {
  MasterRuleAction,
  MasterRuleBookAction,
  RuleAction,
  RuleBookAction,
} from "actions";

import {
  MASTER_RULE,
  MASTER_RULE_BOOK,
  RULE,
  RULE_BOOK,
} from "constants/actionTypes";

import {
  getLastOrderByMasterRuleBookId,
  getLastOrderByRuleBookId,
  getMasterRuleBookById,
  getMasterRuleById,
  getMasterRulesByMasterRuleBookId,
  getRuleBookById,
  getRuleById,
  getRulesByRuleBookId,
} from "selectors";

import {
  getMasterRuleSetsByMasterRuleBookId,
  getRuleSetsByRuleBookId,
} from "selectors/ruleSets";
import { RuleSetAction } from "actions/ruleSets";

function* onCopyRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const rule = getRuleById(ruleId)(state);

  const lastOrder = getLastOrderByRuleBookId(rule.rule_book_id)(state);

  yield put(
    RuleAction.create(
      { ...rule, id: v4(), name: `${rule.name} (Copy)`, order: lastOrder + 1 },
      { changeReason: `Copied from "${rule.name}"`, ...options },
    ),
  );
}

function* onCopyMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRule = getMasterRuleById(masterRuleId)(state);

  const lastOrder = getLastOrderByMasterRuleBookId(masterRule.rule_book_id)(
    state,
  );

  yield put(
    MasterRuleAction.create(
      {
        ...masterRule,
        id: v4(),
        name: `${masterRule.name} (Copy)`,
        order: lastOrder + 1,
      },
      { changeReason: `Copied from "${masterRule.name}"`, ...options },
    ),
  );
}

function decrementRuleOrMasterRuleOrder(thisRule, prevRule) {
  return [
    { ...thisRule, order: prevRule.order },
    {
      ...prevRule,
      order: prevRule.order + 1,
    },
  ];
}

function incrementRuleOrMasterRuleOrder(thisRule, nextRule) {
  return [
    { ...thisRule, order: nextRule.order },
    {
      ...nextRule,
      order: nextRule.order - 1,
    },
  ];
}

function* onMoveUpRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const ruleBookId = getRuleById(ruleId)(state).rule_book_id;
  const rules = getRulesByRuleBookId(ruleBookId)(state);

  const thisIndex = rules.findIndex(rule => rule.id === ruleId);
  const thisRule = rules[thisIndex];
  const prevRule = rules[thisIndex - 1];

  yield all(
    decrementRuleOrMasterRuleOrder(thisRule, prevRule).map(rule =>
      put(
        RuleAction.update(
          { id: rule.id, order: rule.order },
          {
            changeReason: "Updated order",
            ...options,
          },
        ),
      ),
    ),
  );
}

function* onMoveDownRule(action) {
  const { ruleId, options = null } = action;
  const state = yield select();
  const ruleBookId = getRuleById(ruleId)(state).rule_book_id;
  const rules = getRulesByRuleBookId(ruleBookId)(state);

  const thisIndex = rules.findIndex(rule => rule.id === ruleId);
  const thisRule = rules[thisIndex];
  const nextRule = rules[thisIndex + 1];

  yield all(
    incrementRuleOrMasterRuleOrder(thisRule, nextRule).map(rule =>
      put(
        RuleAction.update(
          { id: rule.id, order: rule.order },
          {
            changeReason: "Updated order",
            ...options,
          },
        ),
      ),
    ),
  );
}

function* onMoveUpMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRuleBookId = getMasterRuleById(masterRuleId)(state).rule_book_id;
  const masterRules = getMasterRulesByMasterRuleBookId(masterRuleBookId)(state);

  const currentIndex = masterRules.findIndex(
    masterRule => masterRule.id === masterRuleId,
  );
  const currentMasterRule = masterRules[currentIndex];
  const prevMasterRule = masterRules[currentIndex - 1];

  yield all(
    decrementRuleOrMasterRuleOrder(currentMasterRule, prevMasterRule).map(
      updatedMasterRule =>
        put(
          MasterRuleAction.update(updatedMasterRule, {
            changeReason: "Updated order",
            ...options,
          }),
        ),
    ),
  );
}

function* onMoveDownMasterRule(action) {
  const { masterRuleId, options = null } = action;
  const state = yield select();
  const masterRuleBookId = getMasterRuleById(masterRuleId)(state).rule_book_id;
  const masterRules = getMasterRulesByMasterRuleBookId(masterRuleBookId)(state);

  const currentIndex = masterRules.findIndex(
    masterRule => masterRule.id === masterRuleId,
  );
  const currentMasterRule = masterRules[currentIndex];
  const nextMasterRule = masterRules[currentIndex + 1];

  yield all(
    incrementRuleOrMasterRuleOrder(currentMasterRule, nextMasterRule).map(
      updatedMasterRule =>
        put(
          MasterRuleAction.update(updatedMasterRule, {
            changeReason: "Updated order",
            ...options,
          }),
        ),
    ),
  );
}

/**
 * Generates a mapping of original item IDs to new UUIDs.
 *
 * @param {Array} items - The array of items, each with an `id` property.
 * @returns {Object} An object mapping each item's original `id` to a new UUID.
 */
function createIdReplacementMap(items) {
  return items.reduce((acc, item) => {
    acc[item.id] = v4();
    return acc;
  }, {});
}

/**
 * Prepares an Array of RuleSets for a copy operation of a new Rule Book.
 * Specifically operates on Rule that already exist in the source Rule Book
 */
function copyRuleSets(ruleBookRuleSets, rulesInRuleSets, ruleIdReplacementMap) {
  const ruleSetById = keyBy(ruleBookRuleSets, "id");
  const rulesByRuleSetId = groupBy(rulesInRuleSets, "rule_set_id");
  const ruleSetsCopy = [];

  Object.values(ruleSetById).forEach(ruleSet => {
    const rules = rulesByRuleSetId[ruleSet.id] || [];
    if (!rules.length) {
      return;
    }
    ruleSetsCopy.push({
      ...ruleSet,
      id: undefined,
      // Give it a distinct name if it doesn't yet have a name
      name: ruleSet.name ? ruleSet.name : `${rules[0].name} Rule Set`,
      rules: rules.map(rule => ({
        ...rule,
        rule_set_id: undefined,
        id: ruleIdReplacementMap[rule.id],
      })),
    });
  });

  return ruleSetsCopy;
}

/**
 * Prepares an Array of RuleSets for a copy operation of a new Rule Book.
 * Specifically operates on Rules that do not exist in a Rule Set
 */
function copyRulesWithoutRuleSets(rulesWithoutRuleSets, newIds) {
  const ruleSetsCopy = [];
  const rulesByInputField = groupBy(rulesWithoutRuleSets, "input_field");
  Object.values(rulesByInputField).forEach(rules => {
    ruleSetsCopy.push({
      input_field: rules[0].input_field,
      name: `${rules[0].name} Rule Set`,
      rules: rules.map(rule => ({
        ...rule,
        rule_set_id: undefined,
        input_field: null,
        id: newIds[rule.id],
      })),
    });
  });
  return ruleSetsCopy;
}

/**
 * Repalaces The prerequisite rule IDs in the copied RuleSets as part of a Copy Rule Book operation
 * @param ruleSetsCopy
 * @param idReplacementMap
 */
function rewritePrerequisiteIds(ruleSetsCopy, idReplacementMap) {
  Object.values(ruleSetsCopy).forEach(
    (
      ruleSet: RuleSet & {
        rules: Array<Rule & { copied_prerequisite_rule_ids: string[] }>;
      },
    ) => {
      ruleSet.rules.forEach(rule => {
        rule.rule_set_id = undefined;
        rule.copied_prerequisite_rule_ids = rule.prerequisite_rule_ids.map(
          ruleId => idReplacementMap[ruleId],
        );
        rule.prerequisite_rule_ids = undefined;
      });
    },
  );
}

function copyRuleGeneratorFactory(
  sourceRuleBookIdKey,
  destinationRuleBookIdKey,
  ruleBookGetterSelector,
  ruleBookRulesGetterSelector,
  ruleSetGetterSelector,
  ruleBookOfflineActionCreator,
) {
  return function* onCopyRuleBookGenFn(action) {
    const {
      [sourceRuleBookIdKey]: sourceRuleBookId,
      [destinationRuleBookIdKey]: destinationRuleBookId = v4(),
      options = null,
    } = action;
    const state = yield select();
    const ruleBook = ruleBookGetterSelector(sourceRuleBookId)(state);

    const ruleBookRules =
      ruleBookRulesGetterSelector(sourceRuleBookId)(state) || [];

    const ruleBookRuleSets =
      ruleSetGetterSelector(sourceRuleBookId)(state) || [];

    // To maintain the link between pre-requisite rules, create a lookup.
    const ruleIdReplacementMap = createIdReplacementMap(ruleBookRules);

    const [rulesInRuleSets, rulesWithoutRuleSets] = partition(
      ruleBookRules,
      rule => Boolean(rule.rule_set_id),
    );

    const ruleSetsCopy = [
      // For rules that are in rule sets, we need to copy the rule set first
      ...copyRuleSets(ruleBookRuleSets, rulesInRuleSets, ruleIdReplacementMap),
      // Add the rules that are not in and existing rule set to a new RuleSet, grouped with other Rules sharing the same input field
      ...copyRulesWithoutRuleSets(rulesWithoutRuleSets, ruleIdReplacementMap),
    ];

    // Wire up the Rule Ids from the Prerequisite Rules to the new Rule Ids
    rewritePrerequisiteIds(ruleSetsCopy, ruleIdReplacementMap);

    yield put(
      ruleBookOfflineActionCreator.create(
        {
          ...ruleBook,
          id: destinationRuleBookId,
          name: `${ruleBook.name} (Copy)`,
          rule_sets: ruleSetsCopy,
        },
        { changeReason: `Copied from "${ruleBook.name}"`, ...options },
      ),
    );
  };
}

const onCopyMasterRuleBook = copyRuleGeneratorFactory(
  "sourceMasterRuleBookId",
  "destinationMasterRuleBookId",
  getMasterRuleBookById,
  getMasterRulesByMasterRuleBookId,
  getMasterRuleSetsByMasterRuleBookId,
  MasterRuleBookAction,
);
const onCopyRuleBook = copyRuleGeneratorFactory(
  "sourceRuleBookId",
  "destinationRuleBookId",
  getRuleBookById,
  getRulesByRuleBookId,
  getRuleSetsByRuleBookId,
  RuleBookAction,
);

function* requestRuleDataChanges() {
  // Request all changes to local rule data.
  // Should be called after unsubscribing/synchronizing.
  const state = yield select();

  yield put(
    RuleAction.requestChanges({
      changesSince: state.rules.lastModifiedTimestamp,
    }),
  );
  yield put(
    RuleBookAction.requestChanges({
      changesSince: state.ruleBooks.lastModifiedTimestamp,
    }),
  );
  yield put(
    RuleSetAction.requestChanges({
      changesSince: state.ruleSets.lastModifiedTimestamp,
    }),
  );
}

export default function* rootSaga() {
  yield takeEvery(RULE.COPY.ACTION, onCopyRule);
  yield takeEvery(RULE.MOVE_UP.ACTION, onMoveUpRule);
  yield takeEvery(RULE.MOVE_DOWN.ACTION, onMoveDownRule);

  yield takeEvery(MASTER_RULE_BOOK.COPY.ACTION, onCopyMasterRuleBook);

  yield takeEvery(RULE_BOOK.COPY.ACTION, onCopyRuleBook);

  yield takeEvery(MASTER_RULE.COPY.ACTION, onCopyMasterRule);
  yield takeEvery(MASTER_RULE.MOVE_UP.ACTION, onMoveUpMasterRule);
  yield takeEvery(MASTER_RULE.MOVE_DOWN.ACTION, onMoveDownMasterRule);

  yield takeEvery(
    RULE_BOOK.SYNCHRONIZE_WITH_MASTER_RULE_BOOK.SUCCESS,
    requestRuleDataChanges,
  );
  yield takeEvery(
    MASTER_RULE_BOOK.SYNCHRONIZE_ALL.SUCCESS,
    requestRuleDataChanges,
  );
  yield takeEvery(
    RULE_BOOK.UNSUBSCRIBE_FROM_MASTER_RULE_BOOK.SUCCESS,
    requestRuleDataChanges,
  );
}
